import * as React from 'react'
import { inject, observer } from 'mobx-react'
import { ProductSearchFilter } from '@getgreenline/homi-shared'
import { ProductModels, productApi } from '@getgreenline/products'
import { CurrentCompanyStore } from '../../../../../../stores/CurrentCompanyStore'
import { SelectedProducts, SelectOption } from '../../FilterGroup.types'
import { SelectFilterDropdown } from '../FilterDropdown'
import Tag from './Tag'

export interface ProductDropdownProps {
  currentCompanyStore?: CurrentCompanyStore
  onSelect: (product: ProductModels.IBaseProduct, label: string) => void
  onEmptySelect?: () => void
  value?: string
  style?: React.CSSProperties
  doesNotAllowBatchTrackedProducts?: boolean
  optionFilterFunction?: (products: ProductModels.IBaseProduct[]) => ProductModels.IBaseProduct[]
  isInventoryProducts?: boolean
  isSellableProducts?: boolean
  selectedProducts: SelectedProducts[]
  removeSelectedProduct: (product: { id: string; name: string }) => Promise<void>
  removeAllProducts: () => Promise<void>
}

export interface ProductDropdownState {
  products: ProductModels.IBaseProduct[]
}

/**
 * Product Async Select
 *
 * A more verbose Ant Design component version of the already
 * existin ProductAsyncInput component. Currently there is unncessary
 * prop-drilling to get the functions this component needs. This needs to be
 * cleaned up.
 *
 * @returns {JSX.Element} Ant Design Select Dropdown Component
 * @todo refactor to use hooks once the codebase allows it
 */
@inject('currentCompanyStore')
@observer
class ProductDropdown extends React.Component<ProductDropdownProps, ProductDropdownState> {
  constructor(props: ProductDropdownProps) {
    super(props)
    this.state = {
      products: [],
    }
  }

  componentDidMount() {
    const { value } = this.props
    this.fetchProducts(value)
  }

  fetchProducts = async (value = '') => {
    const company = this.props.currentCompanyStore!.company!
    const filterJson = {
      searchQuery: value,
      selectedSupplierId: undefined,
      selectedCategoryIds: [],
      offset: 0,
      limit: 15,
    }
    const searchFilter = new ProductSearchFilter(filterJson)

    const test: ProductModels.IBaseProduct[] = await productApi
      .getProducts(company.id, searchFilter)
      .then((res) => res.products)

    this.setState({ products: test })

    return test.map((product: ProductModels.IBaseProduct) => ({
      value: product.id,
      label: product.name,
    }))
  }

  get options() {
    return this.state.products.map((product) => ({
      value: product.id,
      key: product.id,
      label: product.parentProductName
        ? `${product.parentProductName} - ${product.name}`
        : product.name,
    }))
  }

  getPreFilteredProducts = () => {
    const { products } = this.state
    const { currentCompanyStore, isInventoryProducts, isSellableProducts } = this.props

    const flattenedProducts = currentCompanyStore!.productStore!.flattenProducts(
      products as ProductModels.INestedProduct[],
    )
    const sellableProducts = currentCompanyStore!.productStore!.getSellableProducts(
      products as ProductModels.INestedProduct[],
    )

    if (isInventoryProducts) {
      return flattenedProducts.filter((p) => p.trackInventory && p.isActive)
    } else if (isSellableProducts) {
      return sellableProducts
    } else {
      return flattenedProducts
    }
  }

  handleOptionFilterLogic = (products: ProductModels.IBaseProduct[]) => {
    const { optionFilterFunction } = this.props
    if (optionFilterFunction) {
      return optionFilterFunction(products)
    } else {
      return products
    }
  }

  handleBatchTrackedFilter = (products: ProductModels.IBaseProduct[]) => {
    const { doesNotAllowBatchTrackedProducts } = this.props
    if (doesNotAllowBatchTrackedProducts) {
      return products.filter((product) => !product.isBatchTracked)
    } else {
      return products
    }
  }

  /**
   * I don't think this logic is necessary, this can be cleaned up but onSelect
   * logic that is being passed here would need to be refactored as well.
   */
  handleOnChange = (option: SelectOption[], products: ProductModels.IBaseProduct[]) => {
    const selectedIds = option.map((op: SelectOption) => op.key)
    const foundProducts = products.filter((product) => selectedIds.includes(product.id))
    foundProducts.forEach((element: ProductModels.IBaseProduct) => {
      this.props.onSelect(element, element.name)
    })
  }

  render() {
    const { selectedProducts, removeSelectedProduct, removeAllProducts } = this.props

    const controlledValues = selectedProducts.map((product) => ({
      value: product.id,
      label: product.name,
    }))
    const preFilterProducts = this.getPreFilteredProducts()
    const filteredProducts = this.handleOptionFilterLogic(preFilterProducts)
    const products = this.handleBatchTrackedFilter(filteredProducts)

    return (
      <div className={'product-dropdown-list'}>
        <SelectFilterDropdown
          async
          width={'100%'}
          mode='multiple'
          label={'Products'}
          placeholder={'Search a product'}
          value={controlledValues.length ? controlledValues : undefined}
          options={this.fetchProducts}
          fetchOnMount
          debounceTimeout={600}
          labelInValue
          bordered={false}
          useCustomTheme={false}
          dropdownClassName={'productDropdownOptions'}
          tagRender={(props) => <Tag {...props} />}
          onDeselect={(option) => {
            removeSelectedProduct({
              id: option.value as string,
              name: option.label as string,
            })
          }}
          onClear={() => removeAllProducts()}
          onChange={(option: SelectOption[]) => this.handleOnChange(option, products)}
        />
      </div>
    )
  }
}

export default ProductDropdown
